// forms/SurveyingForm.tsx
import React, { useState, useEffect} from 'react';
import { BaseFormProps, SurveyingFormData } from '../../../types/FormTypes';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTokenStore from '../../../store/store';
import { 
  faClipboardList, 
  faCalculator,
  faSearch,
  faFileAlt,
  faHome,
  faTools,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  User, 
  AlertTriangle,
  ChevronDown,
  Loader2,
  CheckCircle,
  Edit
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/semantic-ui.css';
import { fetchUserProfile } from '../../../utils/user';

interface SurveyingFormProps extends BaseFormProps<SurveyingFormData> {}

// Accordion Item Component
interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg mb-2">
      <button
        type="button"
        className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h6 className="font-semibold text-sm">{title}</h6>
        <ChevronDown
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-3">
          {children}
        </div>
      )}
    </div>
  );
};

// Fee Scale Table Component
interface FeeScaleTableProps {
  title: string;
  data: Array<{ propertyValue: string; price: string }>;
}

const FeeScaleTable: React.FC<FeeScaleTableProps> = ({ title, data }) => (
  <div className="mb-6">
    <h5 className="font-semibold text-sm mb-3 text-gray-700">{title}</h5>
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-3 py-2 text-left font-medium">Property Value (not exceeding)</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium">Price (including VAT)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="border border-gray-200 px-3 py-2">{row.propertyValue}</td>
              <td className="border border-gray-200 px-3 py-2 font-medium">{row.price}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SurveyingForm: React.FC<SurveyingFormProps> = ({
  initialData,
  errors = {},
  isSubmitting = false,
  onSubmit,
  onCancel,
  themes
}) => {
  // Default form structure
  const getDefaultData = (): SurveyingFormData => ({
    clientDetails: {
      title: '',
      firstName: '',
      lastName: '',
      mobile: '',
      email: '',
      propertyValue: '',
      surveyPropertyPostcode: '',
      notes: ''
    },
    selectedSurveys: {
      privateRICSValuation: false,
      homeSurveyLevel2Report: false,
      homeSurveyLevel3Report: false,
      level2ReportValuation: false,
      level3ReportValuation: false,
      specialistStructuralEngineer: false,
      specialistAsbestosReport: false,
      specialistCCTVDrain: false,
      specialistDampAndTimber: false,
      specialistElectrical: false,
      specialistGas: false,
      specialistHSR: false,
      specialistMundicReport: false,
      specialistPartyWallInspection: false,
      specialistRoofInspection: false,
      snaggingReport: false,
      specialistTree: false,
      specialistWallTie: false
    }
  });

  // Local form state
  const [formData, setFormData] = useState<SurveyingFormData>(() => ({
    ...getDefaultData(),
    ...initialData
  }));
  
  const [currentStep, setCurrentStep] = useState(1);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  // Sync with parent data changes
  useEffect(() => {
    if (initialData) {
      setFormData((prev: SurveyingFormData) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  // Fetch and auto-populate user profile data
  useEffect(() => {
    const getToken = useTokenStore.getState().token; // Get token from store
    
    const loadUserProfile = async () => {
      try {
        if (!getToken) {
          console.log('No token available for user profile fetch');
          return;
        }
        
        const userProfile = await fetchUserProfile(getToken); // Pass token to fetchUserProfile
        if (userProfile) {
          console.log('Auto-populating surveying form with user profile:', userProfile);
          setFormData((prev: SurveyingFormData) => ({
            ...prev,
            clientDetails: {
              ...prev.clientDetails,
              firstName: userProfile.firstName || prev.clientDetails.firstName,
              lastName: userProfile.lastName || prev.clientDetails.lastName,
              email: userProfile.email || prev.clientDetails.email,
              mobile: userProfile.phone || prev.clientDetails.mobile,
            }
          }));
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
  
    // Only fetch if we don't already have user data and we have a token
    const hasUserData = formData.clientDetails.firstName || 
                       formData.clientDetails.lastName || 
                       formData.clientDetails.email || 
                       formData.clientDetails.mobile;
    
    if (!hasUserData && getToken) {
      loadUserProfile();
    }
  }, [formData.clientDetails.firstName, formData.clientDetails.lastName, formData.clientDetails.email, formData.clientDetails.mobile]);

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 1:
        const { title, firstName, lastName, mobile, email, propertyValue, surveyPropertyPostcode } = formData.clientDetails;
        if (!title.trim()) newErrors['client.title'] = 'Title is required';
        if (!firstName.trim()) newErrors['client.firstName'] = 'First name is required';
        if (!lastName.trim()) newErrors['client.lastName'] = 'Last name is required';
        if (!mobile.trim() || mobile.length < 10) newErrors['client.mobile'] = 'Valid mobile number is required';
        if (!email.trim() || !email.includes('@')) newErrors['client.email'] = 'Valid email is required';
        if (!propertyValue.trim()) newErrors['client.propertyValue'] = 'Property value is required';
        if (!surveyPropertyPostcode.trim()) newErrors['client.surveyPropertyPostcode'] = 'Survey property postcode is required';
        break;
      case 2:
        // Check if at least one survey is selected
        const selectedSurveys = Object.values(formData.selectedSurveys);
        if (!selectedSurveys.some(selected => selected)) {
          newErrors['surveys'] = 'Please select at least one survey type';
        }
        break;
      case 3:
        // Review step - no validation needed
        break;
    }
    
    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form field updater
  const updateField = (path: string, value: any) => {
    setFormData((prev: SurveyingFormData) => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key]) current[key] = {};
        current = current[key];
      }
      
      const lastKey = keys[keys.length - 1];
      current[lastKey] = value;
      
      return newData;
    });
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
      // Scroll to top of the form container
      const formContainer = document.querySelector('.flex-1.overflow-y-auto');
      if (formContainer) {
        formContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll to top of the form container
    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
    if (formContainer) {
      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Submit
  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      const requiresPayment = true; // Always requires payment for surveying
      onSubmit(formData, requiresPayment);
    }
  };

  // Combine local and parent errors
  const allErrors = { ...localErrors, ...errors };

  // Fee scales data
  const feeScales = {
    privateRICSValuation: [
      { propertyValue: '£150,000', price: '£270' },
      { propertyValue: '£250,000', price: '£295' },
      { propertyValue: '£350,000', price: '£320' },
      { propertyValue: '£500,000', price: '£345' },
      { propertyValue: '£700,000', price: '£370' },
      { propertyValue: '£850,000', price: '£395' },
      { propertyValue: '£1,000,000', price: '£420' },
      { propertyValue: '£3,000,000', price: '£520' },
      { propertyValue: '£5,000,000', price: '£620' }
    ],
    homeSurveyLevel2Report: [
      { propertyValue: '£250,000', price: '£570' },
      { propertyValue: '£300,000', price: '£630' },
      { propertyValue: '£350,000', price: '£660' },
      { propertyValue: '£400,000', price: '£690' },
      { propertyValue: '£600,000', price: '£780' },
      { propertyValue: '£700,000', price: '£840' },
      { propertyValue: '£800,000', price: '£900' },
      { propertyValue: '£900,000', price: '£1,020' },
      { propertyValue: '£1,000,000', price: '£1,170' },
      { propertyValue: '£3,000,000', price: '£1,500' },
      { propertyValue: '£5,000,000', price: '£2,000' }
    ],
    homeSurveyLevel3Report: [
      { propertyValue: '£250,000', price: '£780' },
      { propertyValue: '£275,000', price: '£810' },
      { propertyValue: '£350,000', price: '£888' },
      { propertyValue: '£450,000', price: '£930' },
      { propertyValue: '£500,000', price: '£990' },
      { propertyValue: '£700,000', price: '£1,140' },
      { propertyValue: '£800,000', price: '£1,350' },
      { propertyValue: '£900,000', price: '£1,500' },
      { propertyValue: '£1,000,000', price: '£1,750' },
      { propertyValue: '£3,000,000', price: '£2,250' },
      { propertyValue: '£5,000,000', price: '£3,500' }
    ],
    level2ReportValuation: [
      { propertyValue: '£250,000', price: '£660' },
      { propertyValue: '£300,000', price: '£720' },
      { propertyValue: '£350,000', price: '£750' },
      { propertyValue: '£400,000', price: '£780' },
      { propertyValue: '£600,000', price: '£870' },
      { propertyValue: '£700,000', price: '£930' },
      { propertyValue: '£800,000', price: '£990' },
      { propertyValue: '£900,000', price: '£1,110' },
      { propertyValue: '£1,000,000', price: '£1,260' },
      { propertyValue: '£3,000,000', price: '£1,590' },
      { propertyValue: '£5,000,000', price: '£2,090' }
    ],
    level3ReportValuation: [
      { propertyValue: '£250,000', price: '£960' },
      { propertyValue: '£275,000', price: '£990' },
      { propertyValue: '£350,000', price: '£1,068' },
      { propertyValue: '£450,000', price: '£1,110' },
      { propertyValue: '£500,000', price: '£1,170' },
      { propertyValue: '£700,000', price: '£1,320' },
      { propertyValue: '£800,000', price: '£1,530' },
      { propertyValue: '£900,000', price: '£1,680' },
      { propertyValue: '£1,000,000', price: '£1,930' },
      { propertyValue: '£3,000,000', price: '£2,430' },
      { propertyValue: '£5,000,000', price: '£3,680' }
    ],
    specialistStructuralEngineer: [
      { propertyValue: '£10,000,000', price: '£1,160' }
    ],
    specialistAsbestosReport: [
      { propertyValue: '£10,000,000', price: '£1,560' }
    ],
    specialistCCTVDrain: [
      { propertyValue: '£10,000,000', price: '£810' }
    ],
    specialistDampAndTimber: [
      { propertyValue: '£10,000,000', price: '£720' }
    ],
    specialistElectrical: [
      { propertyValue: '£10,000,000', price: '£774' }
    ],
    specialistGas: [
      { propertyValue: '£10,000,000', price: '£642' }
    ],
    specialistHSR: [
      { propertyValue: '£10,000,000', price: '£828' }
    ],
    specialistMundicReport: [
      { propertyValue: '£10,000,000', price: '£1,560' }
    ],
    specialistPartyWallInspection: [
      { propertyValue: '£10,000,000', price: '£1,560' }
    ],
    specialistRoofInspection: [
      { propertyValue: '£10,000,000', price: '£1,560' }
    ],
    snaggingReport: [
      { propertyValue: '£150,000', price: '£450' },
      { propertyValue: '£250,000', price: '£525' },
      { propertyValue: '£350,000', price: '£575' },
      { propertyValue: '£500,000', price: '£660' },
      { propertyValue: '£700,000', price: '£760' },
      { propertyValue: '£850,000', price: '£830' },
      { propertyValue: '£1,000,000', price: '£990' },
      { propertyValue: '£3,000,000', price: '£1,450' },
      { propertyValue: '£5,000,000', price: '£2,500' }
    ],
    specialistTree: [
      { propertyValue: '£10,000,000', price: '£846' }
    ],
    specialistWallTie: [
      { propertyValue: '£10,000,000', price: '£702' }
    ]
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <FontAwesomeIcon icon={faClipboardList} className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Surveying Service Request</h3>
              <p className="text-gray-600">Please provide your details and property information</p>
            </div>

            {/* Client Details Form */}
            <div className="space-y-6">
              <h4 className="text-lg font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Client Details
              </h4>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <select
                  value={formData.clientDetails.title}
                  onChange={(e) => updateField('clientDetails.title', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.title'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                >
                  <option value="">Select title</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
                {allErrors['client.title'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.title']}
                  </p>
                )}
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.clientDetails.firstName}
                  onChange={(e) => updateField('clientDetails.firstName', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.firstName'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Enter your first name"
                />
                {allErrors['client.firstName'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.firstName']}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.clientDetails.lastName}
                  onChange={(e) => updateField('clientDetails.lastName', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.lastName'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Enter your last name"
                />
                {allErrors['client.lastName'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.lastName']}
                  </p>
                )}
              </div>

              {/* Mobile */}
              <div>
                <label className="block text-sm font-medium mb-1">Mobile *</label>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: allErrors['client.mobile'] ? '2px solid #ef4444' : '2px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: allErrors['client.mobile'] ? '#fef2f2' : '#FFF',
                    padding: '0.25rem',
                    position: 'relative',
                  }}
                >
                  <PhoneInput
                    country={'gb'}
                    value={formData.clientDetails.mobile}
                    onChange={(phone: string) => updateField('clientDetails.mobile', phone)}
                    enableSearch={true}
                    inputStyle={{
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      fontSize: '1rem',
                      color: '#2D3748',
                      paddingLeft: '48px',
                      backgroundColor: 'transparent',
                    }}
                    inputProps={{
                      name: 'mobile',
                      required: true,
                      placeholder: 'No leading 0 after country code',
                    }}
                    buttonStyle={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      position: 'absolute',
                      left: '4px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      width: '24px',
                      height: '24px',
                    }}
                    dropdownStyle={{
                      position: 'absolute',
                      top: 'calc(100% + 20px)',
                      left: -8,
                      borderRadius: '0.375rem',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #ddd',
                      backgroundColor: '#fff',
                      width: '200px',
                    }}
                    containerStyle={{
                      position: 'relative',
                    }}
                    searchStyle={{
                      margin: '0',
                      width: '85%',
                      height: '30px',
                      textAlign: 'left',
                    }}
                    disableCountryCode={false}
                    countryCodeEditable={false}
                  />
                </div>
                {allErrors['client.mobile'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.mobile']}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.clientDetails.email}
                  onChange={(e) => updateField('clientDetails.email', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.email'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="you@example.com"
                />
                {allErrors['client.email'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.email']}
                  </p>
                )}
              </div>

              {/* Property Value */}
              <div>
                <label className="block text-sm font-medium mb-1">Property Value *</label>
                <input
                  type="text"
                  value={formData.clientDetails.propertyValue}
                  onChange={(e) => updateField('clientDetails.propertyValue', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.propertyValue'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="e.g., £250,000"
                />
                {allErrors['client.propertyValue'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.propertyValue']}
                  </p>
                )}
              </div>

              {/* Survey Property Postcode */}
              <div>
                <label className="block text-sm font-medium mb-1">Survey Property Postcode *</label>
                <input
                  type="text"
                  value={formData.clientDetails.surveyPropertyPostcode}
                  onChange={(e) => updateField('clientDetails.surveyPropertyPostcode', e.target.value.toUpperCase())}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.surveyPropertyPostcode'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="e.g., SW1A 1AA"
                />
                {allErrors['client.surveyPropertyPostcode'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.surveyPropertyPostcode']}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.clientDetails.notes}
                  onChange={(e) => updateField('clientDetails.notes', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Any additional information about your property or requirements..."
                  rows={4}
                />
              </div>
            </div>

            {/* Further Information Accordion */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h4 className="text-lg font-semibold text-center mb-4">Further Information</h4>
              
              <div className="space-y-2">
                <AccordionItem title="Benefits To Client" defaultOpen={false}>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Peace of mind</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Saves time & money</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Client can speak to surveyor</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Surveyor is acting for client unlike with lender valuation</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">All our surveyors are members of the Royal Institute of Chartered Surveyors (RICS)</span>
                    </div>
                  </div>
                </AccordionItem>

                <AccordionItem title="Did You Know" defaultOpen={false}>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-black flex-shrink-0">!</div>
                      <span className="text-sm mt-0.5">3 in 5 purchasers insist on a Homebuyer Report or Building Survey</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-black flex-shrink-0">!</div>
                      <span className="text-sm mt-0.5">RICS has revealed the average repair bill for home purchases is £5750</span>
                    </div>
                  </div>
                </AccordionItem>

                <AccordionItem title="Important" defaultOpen={false}>
                  <div className="space-y-4">
                    <h5 className="font-semibold text-sm mb-2">There are good reasons why you should NOT use the lender's surveyor to do your survey</h5>
                    <div>
                      <h6 className="font-semibold text-sm mb-2">May jeopardise the mortgage application.</h6>
                      <p className="text-sm text-gray-700">
                        If you ask the lender's surveyor to perform a more detailed survey then there is a greater chance that they may find something untoward. If so they will have a duty to inform the lender and this may jeopardise the mortgage application. By using your own surveyor you avoid this risk and ensure the surveyor has no conflict of interest. The surveyor acts solely for you allowing you to decide what, if any, action to take in light of the survey.
                      </p>
                    </div>
                    <div>
                      <h6 className="font-semibold text-sm mb-2">Faster turnaround.</h6>
                      <p className="text-sm text-gray-700">
                        Instructing your own surveyor is usually much quicker. A 7 day turnaround is typical whereas lender's surveyors are generally booked up well in advance. The sooner you know the true condition of the property the sooner you can make informed decisions such as obtaining quotes for remedial work or renegotiating the price.
                      </p>
                    </div>
                  </div>
                </AccordionItem>

                <AccordionItem title="Which Survey |Is Right For Me?" defaultOpen={false}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-gray-300 px-3 py-2 text-left font-medium bg-gray-200">Key Features</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-medium" style={{ backgroundColor: themes?.color1 || '#1E5188', color: themes?.fontColor || '#ffffff' }}>Home Survey Level 2 Report</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-medium bg-gray-200">Home Survey Level 3 Report *</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Independent valuation to help you avoid overpaying for the property.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600 font-bold">*</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Provides rebuild cost for insurance purposes.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600 font-bold">*</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Report provides easy to read traffic light rating.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600">✓</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Identifies any problems that need urgent attention or require further investigation.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600">✓</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Helps avoid unexpected costly repairs after you move in.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600">✓</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Suitable when buying a conventional house, flat or bungalow, built from common building materials.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600">✓</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Identifies potential legal issues that need to be addressed before completing your purchase.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600">✓</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Identifies issues that need to be investigated to prevent serious damage or dangerous conditions.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600">✓</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Gives advice on the steps needed to repair/resolve any defects identified.</td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-red-600">✓</span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Establishes how the construction type materials are used and how these will perform in the future.</td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">Suitable for older properties or extensively modified buildings or a property in need of modernisation.</td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-3 py-2">A more detailed visual inspection of a wider range of issues including a more thorough consideration of the roof space, ground floors and services.</td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                          <td className="border border-gray-300 px-3 py-2 text-center">
                            <span className="text-blue-600">✓</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 space-y-2 text-xs text-gray-600">
                    <p>* Building Surveys do not include a market valuation or rebuilding costs. These are often instructed in with a mortgage lender's basic valuation which will already contain these figures.</p>
                    <p>* Independent valuation and rebuild cost are available upon request, please enquire with the survey team. NB Additional costs apply.</p>
                  </div>
                </AccordionItem>
              </div>
            </div>
          </div>
        );

              case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Select Survey Types</h3>
              <p className="text-gray-600 mt-2">Please select the surveys you would like us to contact you about</p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <h4 className="font-semibold text-blue-800">Important Information</h4>
                </div>
                <p className="text-sm text-blue-700">
                  The Client is aware GOTO Group will contact them using the details provided.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold flex items-center justify-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="w-5 h-5 text-blue-600" />
                Survey Options
              </h4>

              {/* Survey Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'privateRICSValuation', label: 'Private RICS Valuation', icon: faHome },
                  { key: 'homeSurveyLevel2Report', label: 'Home Survey Level 2 Report', icon: faFileAlt },
                  { key: 'homeSurveyLevel3Report', label: 'Home Survey Level 3 Report', icon: faFileAlt },
                  { key: 'level2ReportValuation', label: 'Level 2 Report + Valuation', icon: faFileAlt },
                  { key: 'level3ReportValuation', label: 'Level 3 Report + Valuation', icon: faFileAlt },
                  { key: 'specialistStructuralEngineer', label: 'Specialist - Structural Engineer', icon: faTools },
                  { key: 'specialistAsbestosReport', label: 'Specialist - Asbestos Report', icon: faShieldAlt },
                  { key: 'specialistCCTVDrain', label: 'Specialist - CCTV Drain', icon: faSearch },
                  { key: 'specialistDampAndTimber', label: 'Specialist - Damp and Timber', icon: faTools },
                  { key: 'specialistElectrical', label: 'Specialist - Electrical', icon: faTools },
                  { key: 'specialistGas', label: 'Specialist - Gas', icon: faTools },
                  { key: 'specialistHSR', label: 'Specialist - HSR', icon: faShieldAlt },
                  { key: 'specialistMundicReport', label: 'Specialist - Mundic Report', icon: faFileAlt },
                  { key: 'specialistPartyWallInspection', label: 'Specialist - Party Wall Inspection', icon: faSearch },
                  { key: 'specialistRoofInspection', label: 'Specialist - Roof Inspection', icon: faSearch },
                  { key: 'snaggingReport', label: 'Snagging Report', icon: faFileAlt },
                  { key: 'specialistTree', label: 'Specialist - Tree', icon: faSearch },
                  { key: 'specialistWallTie', label: 'Specialist - Wall Tie', icon: faTools }
                ].map(({ key, label, icon }) => (
                  <div key={key} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.selectedSurveys[key as keyof typeof formData.selectedSurveys] as boolean}
                      onChange={(e) => updateField(`selectedSurveys.${key}`, e.target.checked)}
                      className="w-4 h-4 rounded border-2 transition-colors focus:ring-2 focus:ring-offset-2"
                      style={{
                        borderColor: formData.selectedSurveys[key as keyof typeof formData.selectedSurveys] ? (themes?.color1 || '#1E5188') : '#d1d5db',
                        backgroundColor: formData.selectedSurveys[key as keyof typeof formData.selectedSurveys] ? (themes?.color1 || '#1E5188') : 'transparent',
                        accentColor: themes?.color1 || '#1E5188'
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={icon} className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                  </div>
                ))}
              </div>

              {allErrors['surveys'] && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {allErrors['surveys']}
                </p>
              )}
            </div>

            {/* Fee Scales Accordion */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
              <h4 className="font-bold text-lg mb-4 text-center flex items-center justify-center gap-2">
                <FontAwesomeIcon icon={faCalculator} className="w-5 h-5 text-blue-600" />
                Fee Scales*
              </h4>
              <p className="text-sm text-gray-600 text-center mb-6">
                The prices below are for guidance only.
              </p>
              
              <div className="space-y-2">
                <AccordionItem title="Section 1: Private RICS Valuation - Fee Scales">
                  <FeeScaleTable title="Private RICS Valuation" data={feeScales.privateRICSValuation} />
                </AccordionItem>
                
                <AccordionItem title="Section 2: Home Survey Level 2 Report - Fee Scales">
                  <FeeScaleTable title="Home Survey Level 2 Report" data={feeScales.homeSurveyLevel2Report} />
                </AccordionItem>
                
                <AccordionItem title="Section 3: Home Survey Level 3 Report - Fee Scales">
                  <FeeScaleTable title="Home Survey Level 3 Report" data={feeScales.homeSurveyLevel3Report} />
                </AccordionItem>
                
                <AccordionItem title="Section 4: Level 2 Report + Valuation - Fee Scales">
                  <FeeScaleTable title="Level 2 Report + Valuation" data={feeScales.level2ReportValuation} />
                </AccordionItem>
                
                <AccordionItem title="Section 5: Level 3 Report + Valuation - Fee Scales">
                  <FeeScaleTable title="Level 3 Report + Valuation" data={feeScales.level3ReportValuation} />
                </AccordionItem>
                
                <AccordionItem title="Section 6: Specialist - Structural Engineer - Fee Scales">
                  <FeeScaleTable title="Specialist - Structural Engineer" data={feeScales.specialistStructuralEngineer} />
                </AccordionItem>
                
                <AccordionItem title="Section 7: Specialist - Asbestos Report - Fee Scales">
                  <FeeScaleTable title="Specialist - Asbestos Report" data={feeScales.specialistAsbestosReport} />
                </AccordionItem>
                
                <AccordionItem title="Section 8: Specialist - CCTV Drain - Fee Scales">
                  <FeeScaleTable title="Specialist - CCTV Drain" data={feeScales.specialistCCTVDrain} />
                </AccordionItem>
                
                <AccordionItem title="Section 9: Specialist - Damp and Timber - Fee Scales">
                  <FeeScaleTable title="Specialist - Damp and Timber" data={feeScales.specialistDampAndTimber} />
                </AccordionItem>
                
                <AccordionItem title="Section 10: Specialist - Electrical - Fee Scales">
                  <FeeScaleTable title="Specialist - Electrical" data={feeScales.specialistElectrical} />
                </AccordionItem>
                
                <AccordionItem title="Section 11: Specialist - Gas - Fee Scales">
                  <FeeScaleTable title="Specialist - Gas" data={feeScales.specialistGas} />
                </AccordionItem>
                
                <AccordionItem title="Section 12: Specialist - HSR - Fee Scales">
                  <FeeScaleTable title="Specialist - HSR" data={feeScales.specialistHSR} />
                </AccordionItem>
                
                <AccordionItem title="Section 13: Specialist - Mundic Report - Fee Scales">
                  <FeeScaleTable title="Specialist - Mundic Report" data={feeScales.specialistMundicReport} />
                </AccordionItem>
                
                <AccordionItem title="Section 14: Specialist - Party Wall Inspection - Fee Scales">
                  <FeeScaleTable title="Specialist - Party Wall Inspection" data={feeScales.specialistPartyWallInspection} />
                </AccordionItem>
                
                <AccordionItem title="Section 15: Specialist - Roof Inspection - Fee Scales">
                  <FeeScaleTable title="Specialist - Roof Inspection" data={feeScales.specialistRoofInspection} />
                </AccordionItem>
                
                <AccordionItem title="Section 16: Snagging Report - Fee Scales">
                  <FeeScaleTable title="Snagging Report" data={feeScales.snaggingReport} />
                </AccordionItem>
                
                <AccordionItem title="Section 17: Specialist - Tree - Fee Scales">
                  <FeeScaleTable title="Specialist - Tree" data={feeScales.specialistTree} />
                </AccordionItem>
                
                <AccordionItem title="Section 18: Specialist - Wall Tie - Fee Scales">
                  <FeeScaleTable title="Specialist - Wall Tie" data={feeScales.specialistWallTie} />
                </AccordionItem>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Review your answers</h3>
              <p className="text-gray-600 mt-2">Please review your information before proceeding</p>
            </div>

            {/* Client Details Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Client Details
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Title</label>
                  <p className="text-base">{formData.clientDetails.title}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">First Name</label>
                  <p className="text-base">{formData.clientDetails.firstName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Last Name</label>
                  <p className="text-base">{formData.clientDetails.lastName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Mobile</label>
                  <p className="text-base">{formData.clientDetails.mobile}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="text-base">{formData.clientDetails.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Property Value</label>
                  <p className="text-base">{formData.clientDetails.propertyValue}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Survey Property Postcode</label>
                  <p className="text-base">{formData.clientDetails.surveyPropertyPostcode}</p>
                </div>
                {formData.clientDetails.notes && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-600">Notes</label>
                    <p className="text-base">{formData.clientDetails.notes}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(1);
                    // Scroll to top of the form container
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Client Details
                </button>
              </div>
            </div>

            {/* Selected Surveys Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="w-5 h-5 text-green-600" />
                Selected Surveys
              </h4>
              
              <div className="space-y-2">
                {Object.entries(formData.selectedSurveys).map(([key, selected]) => {
                  if (selected) {
                    const surveyLabels: Record<string, string> = {
                      privateRICSValuation: 'Private RICS Valuation',
                      homeSurveyLevel2Report: 'Home Survey Level 2 Report',
                      homeSurveyLevel3Report: 'Home Survey Level 3 Report',
                      level2ReportValuation: 'Level 2 Report + Valuation',
                      level3ReportValuation: 'Level 3 Report + Valuation',
                      specialistStructuralEngineer: 'Specialist - Structural Engineer',
                      specialistAsbestosReport: 'Specialist - Asbestos Report',
                      specialistCCTVDrain: 'Specialist - CCTV Drain',
                      specialistDampAndTimber: 'Specialist - Damp and Timber',
                      specialistElectrical: 'Specialist - Electrical',
                      specialistGas: 'Specialist - Gas',
                      specialistHSR: 'Specialist - HSR',
                      specialistMundicReport: 'Specialist - Mundic Report',
                      specialistPartyWallInspection: 'Specialist - Party Wall Inspection',
                      specialistRoofInspection: 'Specialist - Roof Inspection',
                      snaggingReport: 'Snagging Report',
                      specialistTree: 'Specialist - Tree',
                      specialistWallTie: 'Specialist - Wall Tie'
                    };
                    
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm">{surveyLabels[key]}</span>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(2);
                    // Scroll to top of the form container
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Survey Selection
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-6">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step <= currentStep
                  ? 'shadow-lg'
                  : 'bg-gray-200 text-gray-600'
              }`}
              style={{
                backgroundColor: step <= currentStep ? (themes?.color1 || '#1E5188') : undefined,
                color: step <= currentStep ? (themes?.fontColor || '#ffffff') : undefined
              }}
            >
              {step < currentStep ? '✓' : step}
            </div>
            {step < 3 && (
              <div
                className={`w-16 h-2 mx-2 rounded-full transition-all ${
                  step < currentStep ? 'shadow-sm' : 'bg-gray-200'
                }`}
                style={{
                  backgroundColor: step < currentStep ? (themes?.color1 || '#1E5188') : undefined
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {renderStep()}
      </div>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="flex justify-between pt-6 border-t bg-white">
        <button
          type="button"
          onClick={currentStep === 1 ? onCancel : handleBack}
          disabled={isSubmitting}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors font-medium"
        >
          {currentStep === 1 ? 'Cancel' : '← Back'}
        </button>

        <button
          type="button"
          onClick={currentStep === 3 ? handleSubmit : handleNext}
          disabled={isSubmitting}
          className="px-6 py-3 rounded-lg disabled:opacity-50 transition-all font-bold shadow-lg hover:shadow-xl transform hover:scale-105"
          style={{ 
            backgroundColor: themes?.color1 || '#1E5188',
            color: themes?.fontColor || '#ffffff'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color2 || themes?.color1 || '#1E5188';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color1 || '#1E5188';
            }
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Processing...
            </span>
          ) : currentStep === 3 ? (
            'Confirm and Submit To Our Partner'
          ) : (
            'Next →'
          )}
        </button>
      </div>
    </div>
  );
};

export default SurveyingForm; 